import dayjs from 'dayjs'
import type {
  DetailedReadingsSelection,
  DetailedReadingsSource,
} from '@/services/detailedReadingsService'

export const DETAILED_READINGS_REPORT = 'קריאות מפורטות'

export const opensDetailedReadings = (params: URLSearchParams) =>
  params.get('report') === 'detailedReadings'

export type SourceInput =
  | { source: 'waterCards' | 'meterNumbers'; text: string }
  | { source: 'waterTree'; groupIds: string[] }
  | { source: 'usageTypes'; usageTypeIds: string[] }

export type DetailedReadingsForm = {
  from: string
  to: string
  input: SourceInput
}

type DetailedReadingsSearch = {
  from: string
  to: string
  selection: DetailedReadingsSelection
}

/** The sources in the order the form offers them, the first one by default. */
export const SOURCE_LABELS: Record<DetailedReadingsSource, string> = {
  waterCards: 'כרטיסי מים',
  meterNumbers: 'מספרי מדים',
  waterTree: 'עץ מים',
  usageTypes: 'סוג שימוש',
}

export const isSource = (
  value: string | null
): value is DetailedReadingsSource =>
  value !== null && Object.hasOwn(SOURCE_LABELS, value)

const INTEGER = /^\d+$/

const splitList = (text: string): string[] =>
  text
    .split(/[,\n]/)
    .map(value => value.trim())
    .filter(Boolean)

export const nonNumbers = (text: string): string[] =>
  splitList(text).filter(value => !INTEGER.test(value))

export const inputFor = (
  source: DetailedReadingsSource,
  values: string[] = []
): SourceInput => {
  switch (source) {
    case 'waterCards':
    case 'meterNumbers':
      return { source, text: values.join(', ') }
    case 'waterTree':
      return { source, groupIds: values }
    case 'usageTypes':
      return { source, usageTypeIds: values }
  }
}

const selectionOf = (input: SourceInput): DetailedReadingsSelection | null => {
  switch (input.source) {
    case 'waterCards':
    case 'meterNumbers': {
      const values = splitList(input.text)
      return values.length > 0 && values.every(value => INTEGER.test(value))
        ? { source: input.source, values }
        : null
    }
    case 'waterTree':
      return input.groupIds.length > 0
        ? { source: 'waterTree', values: input.groupIds }
        : null
    case 'usageTypes':
      return input.usageTypeIds.length > 0
        ? { source: 'usageTypes', values: input.usageTypeIds }
        : null
  }
}

export const datesReversed = ({ from, to }: DetailedReadingsForm) =>
  from !== '' && to !== '' && from > to

/** The search the form describes, or null while it is incomplete. */
export const searchOf = (
  form: DetailedReadingsForm
): DetailedReadingsSearch | null => {
  const selection = selectionOf(form.input)
  if (!form.from || !form.to || datesReversed(form) || !selection) return null
  return { from: form.from, to: form.to, selection }
}

const dateParam = (value: string | null) =>
  value && dayjs(value).format('YYYY-MM-DD') === value ? value : ''

/** ?from=YYYY-MM-DD&to=YYYY-MM-DD&source=<source>&values=<comma-separated> */
export const formFromUrl = (params: URLSearchParams): DetailedReadingsForm => {
  const source = params.get('source')
  return {
    from: dateParam(params.get('from')),
    to: dateParam(params.get('to')),
    input: inputFor(
      isSource(source) ? source : 'waterCards',
      splitList(params.get('values') ?? '')
    ),
  }
}
